import React, { useCallback, useMemo } from 'react';

import cn from 'classnames';

import Button from '@/components/Button';
import Card from '@/components/Card';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import { useAppRouter } from '@/hooks/useAppRouter';
import useAppUiData from '@/hooks/useAppUiData';
import { useMessages } from '@/hooks/useMessages';

import RichUI from '@/ui-kit/rich-text/RichUI/RichUI';

import styles from './ClaimBookingStep.module.scss';
import { getCurrentClaimedSailor } from '../helpers/claimBookingHelper';
import { StepData } from '../types/ClaimBookingTypes';

const CLAIM_SUCCESS_MSG_KEYS = {
  loginButton: 'SignInForm.loginButton',
};

export const CLAIM_DEFAULT_KEYS = {
  loginButton: 'Login',
};

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

type ClaimBookingOTPSuccessProps = {
  tabId: string;
  stepData: StepData;
  onClose: () => void;
};

const ClaimBookingOTPSuccess: React.FC<ClaimBookingOTPSuccessProps> = ({ tabId, onClose, stepData }) => {
  const appUiData = useAppUiData();
  const router = useAppRouter();

  const claimSuccessLabels = useMessages(CLAIM_SUCCESS_MSG_KEYS, true);
  const { loginButton } = claimSuccessLabels;
  const LoginButtonText = loginButton || CLAIM_DEFAULT_KEYS.loginButton;

  const handleLoginClick = useCallback(() => {
    router.push(`/auth/v1/signin`);
    onClose();
  }, [router, onClose]);

  const matchedSailor = useMemo(() => getCurrentClaimedSailor(stepData), [stepData]);

  const birthday = useMemo(() => {
    const dobString = matchedSailor?.dob || stepData.selectedDOB || '';
    const [year, month, day] = dobString.split('-');

    const parsedYear = parseInt(year || '', 10);
    const parsedMonth = parseInt(month || '', 10) - 1;
    const parsedDay = parseInt(day || '', 10);

    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth) || Number.isNaN(parsedDay)) {
      return '';
    }

    const date = new Date(parsedYear, parsedMonth, parsedDay);

    return date.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
  }, [matchedSailor?.dob]);

  const getSailorsEmail = () => {
    const email = stepData.selectedEmail || matchedSailor?.email;

    if (email) {
      return <div>{email}</div>;
    }

    return null;
  };

  return (
    <WizardStep tabId={tabId}>
      <div className={cn(styles.successContainer, styles.successContainerMd)}>
        <h2 className={styles.title}>
          <RichUI code={appUiData[appUiIds.claimBooking.otpSuccessTitle]} useRichText={false} />
        </h2>
        <h3 className={styles.sailorDetailsTitle}>
          Check your reservation details carefully and then [Login again] to complete the process.
        </h3>
        <div className={styles.successLayout}>
          <Card className={styles.sailorDetailsCard}>
            <div className={styles.sailorDetailsDescription}>{appUiData[appUiIds.claimBooking.otpSuccessSubtitle]}</div>
            <div className={styles.sailorInfo}>
              {matchedSailor?.name?.first && (
                <div className={styles.sailorName}>
                  {matchedSailor.name.first} {matchedSailor.name.last}
                </div>
              )}
              {getSailorsEmail()}
              {birthday && <div>{birthday}</div>}
              {(matchedSailor?.sex || matchedSailor?.citizenship) && (
                <div>
                  {matchedSailor.sex}
                  {matchedSailor.sex && matchedSailor.citizenship && ', '}
                  {matchedSailor.citizenship}
                </div>
              )}
            </div>
            <div className={styles.contactSailorServices}>
              <RichUI code={appUiData[appUiIds.claimBooking.otpSuccessIncorrectDetails]} useRichText={false} />
            </div>
          </Card>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <Button className={styles.gotoBtn} variant="primary" onClick={handleLoginClick}>
          {LoginButtonText}
        </Button>
      </div>
    </WizardStep>
  );
};

export default ClaimBookingOTPSuccess;
